import { useMemo, useState } from 'react';
import {
  CheckCircle2,
  ClipboardCopy,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import {
  NUMBER_BASE_LIMITS,
  parseNumber,
  toBase,
  toTwosComplement32,
  type ParseResult,
} from '../lib/numberBase';
import { ToolHeader } from '../components/ToolHeader';

const STANDARD_BASES: ReadonlyArray<{ base: number; label: string }> = [
  { base: 2, label: 'Bin (2)' },
  { base: 8, label: 'Oct (8)' },
  { base: 10, label: 'Dec (10)' },
  { base: 16, label: 'Hex (16)' },
];

const FROM_BASE_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 10, label: '十进制 (10)' },
  { value: 16, label: '十六进制 (16)' },
  { value: 2, label: '二进制 (2)' },
  { value: 8, label: '八进制 (8)' },
];

/**
 * `/tools/convert/number-base` — integer base converter.
 *
 * Layout: input row → 4 standard outputs → custom base output → optional
 * 32-bit two's-complement bit visualizer (collapsed by default to keep
 * the page compact for the common case of "just want hex ↔ decimal").
 */
export default function NumberBaseTool(): JSX.Element {
  const [input, setInput] = useState('');
  const [fromBase, setFromBase] = useState<number>(10);
  const [autoPrefix, setAutoPrefix] = useState(true);
  const [customBase, setCustomBase] = useState<number>(36);
  const [uppercase, setUppercase] = useState(true);
  const [showBits, setShowBits] = useState(false);

  const result: ParseResult = useMemo(
    () => parseNumber(input, fromBase, autoPrefix),
    [input, fromBase, autoPrefix],
  );

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <ToolHeader
        toolId="number-base"
        category="convert"
        description="整数 2 / 8 / 10 / 16 + 自定义进制（2–36）实时互转。基于 BigInt，超过 2^53 也不丢精度。"
      />

      <section className="card-pad flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="input flex-1 font-mono"
            placeholder="0xFF / 0b1010 / 0o17 / 任意进制数字"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            spellCheck={false}
          />
          <label className="flex items-center gap-1 text-xs text-ink-muted">
            解读为
            <select
              className="select py-1"
              value={fromBase}
              onChange={(e) => setFromBase(Number(e.target.value))}
              disabled={autoPrefix && hasPrefix(input)}
            >
              {FROM_BASE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="inline-flex items-center gap-1 text-xs text-ink-muted">
            <input
              type="checkbox"
              checked={autoPrefix}
              onChange={(e) => setAutoPrefix(e.target.checked)}
            />
            自动识别前缀
          </label>
          <label className="inline-flex items-center gap-1 text-xs text-ink-muted">
            <input
              type="checkbox"
              checked={uppercase}
              onChange={(e) => setUppercase(e.target.checked)}
            />
            大写
          </label>
        </div>
        {result.ok && result.prefixStripped && (
          <p className="text-[11px] text-ink-dim">
            已识别前缀，按 base {result.detected} 解析。
          </p>
        )}
        {!result.ok && input.length > 0 && (
          <p className="inline-flex items-center gap-1 text-sm text-rose-600 dark:text-rose-400">
            <XCircle className="h-3.5 w-3.5" />
            {result.error}
          </p>
        )}
      </section>

      {result.ok && result.value !== undefined && (
        <>
          <section className="card-pad flex flex-col gap-1">
            <header className="text-[11px] uppercase tracking-wide text-ink-dim">标准进制</header>
            {STANDARD_BASES.map((b) => (
              <BaseRow
                key={b.base}
                label={b.label}
                value={toBase(result.value!, b.base, { uppercase })}
                highlight={b.base === result.detected}
              />
            ))}
          </section>

          <section className="card-pad flex flex-col gap-2">
            <header className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-ink-dim">
              自定义进制
              <select
                className="select py-0.5 text-xs"
                value={customBase}
                onChange={(e) => setCustomBase(Number(e.target.value))}
              >
                {Array.from(
                  { length: NUMBER_BASE_LIMITS.MAX_BASE - NUMBER_BASE_LIMITS.MIN_BASE + 1 },
                  (_, i) => NUMBER_BASE_LIMITS.MIN_BASE + i,
                ).map((b) => (
                  <option key={b} value={b}>
                    Base {b}
                  </option>
                ))}
              </select>
            </header>
            <BaseRow
              label={`Base ${customBase}`}
              value={toBase(result.value, customBase, { uppercase })}
            />
          </section>

          <section className="card-pad flex flex-col gap-2">
            <header className="flex items-center justify-between text-[11px] uppercase tracking-wide text-ink-dim">
              <span>位运算可视化（32 位）</span>
              <button
                type="button"
                className="text-[11px] normal-case text-ink-muted hover:text-accent"
                onClick={() => setShowBits((s) => !s)}
              >
                {showBits ? '收起' : '展开'}
              </button>
            </header>
            {showBits && <BitView value={result.value} />}
          </section>
        </>
      )}
    </div>
  );
}

function hasPrefix(s: string): boolean {
  const trimmed = s.trim().replace(/^[-+]/, '');
  const lower = trimmed.slice(0, 2).toLowerCase();
  return lower === '0x' || lower === '0b' || lower === '0o';
}

function BaseRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}): JSX.Element {
  return (
    <div
      className={clsx(
        'flex items-center gap-2 rounded px-2 py-0.5 text-[12.5px]',
        highlight && 'bg-accent/5',
      )}
    >
      <span className="w-24 shrink-0 text-ink-muted">{label}</span>
      <span className="min-w-0 flex-1 break-all font-mono text-ink">{value}</span>
      <CopyButton value={value} />
    </div>
  );
}

function BitView({ value }: { value: bigint }): JSX.Element {
  const bits = toTwosComplement32(value);
  if (bits === null) {
    return (
      <p className="text-[12px] text-ink-dim">
        — 数值超出 32 位范围（[-2³¹, 2³² − 1]），位可视化已隐藏 —
      </p>
    );
  }
  return (
    <div className="overflow-x-auto font-mono text-[11px]">
      <div className="flex">
        {bits.split('').map((b, i) => {
          const idx = 31 - i;
          return (
            <div
              key={idx}
              className="flex w-7 flex-col items-center border-r border-line/40 last:border-r-0"
            >
              <span className="text-ink-dim">{idx}</span>
              <span
                className={clsx(
                  'w-full text-center',
                  b === '1' ? 'bg-accent/20 text-ink' : 'text-ink-muted',
                )}
              >
                {b}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CopyButton({ value }: { value: string }): JSX.Element {
  const [done, setDone] = useState(false);
  const onClick = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      window.setTimeout(() => setDone(false), 1000);
    } catch {
      /* ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded p-1 text-ink-dim hover:bg-bg-soft hover:text-accent"
      title="复制"
    >
      {done ? (
        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
      ) : (
        <ClipboardCopy className="h-3 w-3" />
      )}
    </button>
  );
}
